import { accountApi } from "../../config/apiConfig";
import {
  ConfirmSignedResponse,
  ContractAnalyticsQuery,
  ContractTimelineResponse,
  HubSignResponse,
  LogDelayReasonPayload,
  LogReminderPayload,
  MarkEsignSentResponse,
  SendContractResponse,
  SigningAnalytics,
  UploadSignedResponse,
} from "../../types/onboarding";

// Contract-signing admin endpoints (acc-management CWA-78). All go through
// accountApi (Bearer token auto-attached). snake_case wire format. Paths + DTO
// field names verified against ContractSigningAdminController on the e-sign
// branch (all request/response DTOs use @JsonNaming(SnakeCase)).

// POST /admin/prospects/{id}/contract/send — email the agreement PDF (MANUAL).
// → CONTRACT_SENT.
export const sendContract = async (
  id: string
): Promise<SendContractResponse> => {
  const response = await accountApi.post(`admin/prospects/${id}/contract/send`);
  return response.data;
};

// POST /admin/prospects/{id}/contract/upload-signed — raw signed PDF bytes
// (application/octet-stream, NOT multipart). → CONTRACT_SIGNED (MANUAL).
export const uploadSignedContract = async (
  id: string,
  pdfBytes: ArrayBuffer | Blob
): Promise<UploadSignedResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/contract/upload-signed`,
    pdfBytes,
    { headers: { "Content-Type": "application/octet-stream" } }
  );
  return response.data;
};

// POST /admin/prospects/{id}/contract/esign-sent — mark Google eSign as sent
// (DIGITAL). → ESIGN_AWAITED. Returns the draft/document link ops opened.
export const markEsignSent = async (
  id: string
): Promise<MarkEsignSentResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/contract/esign-sent`
  );
  return response.data;
};

// POST /admin/prospects/{id}/contract/confirm-signed — confirm digital signing
// by the signed document's stored link. → CONTRACT_SIGNED (DIGITAL).
//
// The backend reads signed_pdf_drive_url: contracts are still on Google Drive,
// and it takes the Drive file ID from the link. Sending it as
// signed_contract_url (the planned CWA-118 name) made every confirm a 400.
export const confirmSigned = async (
  id: string,
  signedContractUrl: string
): Promise<ConfirmSignedResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/contract/confirm-signed`,
    { signed_pdf_drive_url: signedContractUrl }
  );
  return response.data;
};

// POST /admin/prospects/{id}/contract/send-hub-signing — send the Hub signing
// link (CLICK_TO_SIGN). → CONTRACT_SENT; the customer signs in the Hub.
export const sendHubSigningLink = async (
  id: string
): Promise<HubSignResponse> => {
  const response = await accountApi.post(
    `admin/prospects/${id}/contract/send-hub-signing`
  );
  return response.data;
};

// POST /admin/prospects/{id}/contract/log-reminder — log that a reminder was sent.
export const logReminder = async (
  id: string,
  payload: LogReminderPayload
): Promise<void> => {
  await accountApi.post(`admin/prospects/${id}/contract/log-reminder`, payload);
};

// POST /admin/prospects/{id}/contract/log-delay — log a delay reason.
export const logDelayReason = async (
  id: string,
  payload: LogDelayReasonPayload
): Promise<void> => {
  await accountApi.post(`admin/prospects/${id}/contract/log-delay`, payload);
};

// GET /admin/prospects/{id}/timeline — chronological contract activity.
export const getContractTimeline = async (
  id: string
): Promise<ContractTimelineResponse> => {
  const response = await accountApi.get(`admin/prospects/${id}/timeline`);
  return response.data;
};

// GET /admin/contracts/analytics — aggregate signing analytics, optionally
// filtered by signature method and date range.
export const getContractAnalytics = async (
  query: ContractAnalyticsQuery = {}
): Promise<SigningAnalytics> => {
  const params = new URLSearchParams();
  if (query.signMethod) params.set("signMethod", query.signMethod);
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  const qs = params.toString();
  const response = await accountApi.get(
    `admin/contracts/analytics${qs ? `?${qs}` : ""}`
  );
  return response.data;
};
